import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import { SchemaValidationFailedError } from '@kilat/shared';

/**
 * Sprint 118 — Typed wrappers for the super-admin tenant-management
 * callables. The wizard at `/superadmin/tenants` uses both:
 *   - `checkTenantSlugAvailability` for live "✓ available" on the
 *     review step (lets the super-admin tweak the business name if the
 *     auto-derived slug collided)
 *   - `adminCreateTenant` for the actual commit
 *
 * `getAgentByCode` is also re-exported here as a typed wrapper so the
 * wizard's "attribute to agent" step can show the agent's display name
 * before commit (same UX as the marketing /signup page).
 */

// ── adminCreateTenant ────────────────────────────────────────────────────

export const AdminCreateTenantInputSchema = z.object({
  platformBrandId: z.string().min(1),
  businessName: z.string().min(1).max(100),
  ownerName: z.string().min(1).max(100),
  ownerPhone: z.string().min(8).max(20),
  ownerEmail: z.string().email().optional(),
  locale: z.enum(['en', 'ms', 'zh', 'ta']).default('en'),
  agentCode: z.string().optional(),
});
export type AdminCreateTenantInput = z.infer<typeof AdminCreateTenantInputSchema>;

export const AdminCreateTenantOutputSchema = z.object({
  tenantId: z.string(),
  slug: z.string(),
  ownerUid: z.string(),
  /** `true` when the Cloud Function created a fresh placeholder Firebase
   *  Auth user for the owner (no existing user matched the phone).
   *  `false` when an existing user was linked. The wizard's success card
   *  tells the super-admin which path happened. */
  isNewOwner: z.boolean(),
});
export type AdminCreateTenantOutput = z.infer<typeof AdminCreateTenantOutputSchema>;

const adminCreateTenantFn: HttpsCallable<AdminCreateTenantInput, AdminCreateTenantOutput> =
  httpsCallable(functions, 'adminCreateTenant');

export async function callAdminCreateTenant(
  input: AdminCreateTenantInput,
): Promise<AdminCreateTenantOutput> {
  const parsed = AdminCreateTenantInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('adminCreateTenant.input', parsed.error.flatten());
  }
  const result = await adminCreateTenantFn(parsed.data);
  const out = AdminCreateTenantOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('adminCreateTenant.output', out.error.flatten());
  }
  return out.data;
}

// ── checkTenantSlugAvailability ──────────────────────────────────────────

export const CheckSlugInputSchema = z.object({
  platformBrandId: z.string().min(1),
  slug: z.string().min(3).max(40),
});
export type CheckSlugInput = z.infer<typeof CheckSlugInputSchema>;

export const CheckSlugOutputSchema = z.object({
  available: z.boolean(),
});
export type CheckSlugOutput = z.infer<typeof CheckSlugOutputSchema>;

const checkSlugFn: HttpsCallable<CheckSlugInput, CheckSlugOutput> = httpsCallable(
  functions,
  'checkTenantSlugAvailability',
);

export async function callCheckTenantSlugAvailability(
  input: CheckSlugInput,
): Promise<CheckSlugOutput> {
  const parsed = CheckSlugInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError(
      'checkTenantSlugAvailability.input',
      parsed.error.flatten(),
    );
  }
  const result = await checkSlugFn(parsed.data);
  const out = CheckSlugOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError(
      'checkTenantSlugAvailability.output',
      out.error.flatten(),
    );
  }
  return out.data;
}

// ── getAgentByCode (re-exported public callable for the agent-attribute step) ─

export const GetAgentByCodeInputSchema = z.object({
  code: z.string().min(3).max(20),
  originHost: z.string().min(1),
});
export type GetAgentByCodeInput = z.infer<typeof GetAgentByCodeInputSchema>;

export const GetAgentByCodeOutputSchema = z.object({
  code: z.string(),
  name: z.string(),
  platformBrandId: z.string(),
  status: z.string(),
});
export type GetAgentByCodeOutput = z.infer<typeof GetAgentByCodeOutputSchema>;

const getAgentByCodeFn: HttpsCallable<GetAgentByCodeInput, GetAgentByCodeOutput> = httpsCallable(
  functions,
  'getAgentByCode',
);

export async function callGetAgentByCode(
  input: GetAgentByCodeInput,
): Promise<GetAgentByCodeOutput> {
  const parsed = GetAgentByCodeInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('getAgentByCode.input', parsed.error.flatten());
  }
  const result = await getAgentByCodeFn(parsed.data);
  const out = GetAgentByCodeOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('getAgentByCode.output', out.error.flatten());
  }
  return out.data;
}
